import { AbstractControl, FormArray, FormGroup } from "@angular/forms";

/**
 * Walks a form (or sub-form) and returns the path of the first invalid
 * leaf control, e.g. ["charges", "0", "quantity"]. Returns null when
 * the form is valid.
 */
export function findFirstInvalidPath(
  control: AbstractControl,
  path: string[] = [],
): string[] | null {
  if (!control || control.valid || control.disabled) return null;
  if (control instanceof FormGroup) {
    for (const name of Object.keys(control.controls)) {
      const child = control.controls[name];
      const found = findFirstInvalidPath(child, [...path, name]);
      if (found) return found;
    }
    return path.length ? path : null;
  }
  if (control instanceof FormArray) {
    for (let i = 0; i < control.length; i++) {
      const child = control.at(i);
      const found = findFirstInvalidPath(child, [...path, String(i)]);
      if (found) return found;
    }
    return path.length ? path : null;
  }
  return path;
}

/**
 * Counts the invalid leaf controls under a form so the caller can
 * surface a "N fields need attention" summary.
 */
export function countInvalidFields(control: AbstractControl): number {
  if (!control || control.valid || control.disabled) return 0;
  if (control instanceof FormGroup) {
    return Object.values(control.controls).reduce(
      (sum, child) => sum + countInvalidFields(child),
      0,
    );
  }
  if (control instanceof FormArray) {
    return control.controls.reduce(
      (sum, child) => sum + countInvalidFields(child),
      0,
    );
  }
  return 1;
}

/**
 * Marks the form (and every descendant) as touched, then scrolls the
 * first invalid control into view and tries to focus it. Returns the
 * resolved path so callers can log/inspect.
 */
export function focusFirstInvalid(
  form: AbstractControl,
  hostEl?: Element | null,
): string[] | null {
  // Force inline error rendering by marking touched + dirty.
  form.markAllAsTouched();
  if (form instanceof FormGroup || form instanceof FormArray) {
    form.markAsDirty();
  }

  const path = findFirstInvalidPath(form);
  if (!path) return null;

  // Try a few selectors that match how Taiga UI / native inputs are
  // wired into the templates in this codebase.
  const root: Element | Document = hostEl || document;
  const selectorCandidates = path.length
    ? [
        `[formControlName="${escapeSelector(path[path.length - 1])}"]`,
        `[name="${escapeSelector(path[path.length - 1])}"]`,
      ]
    : [];

  for (const selector of selectorCandidates) {
    const el = root.querySelector(selector) as HTMLElement | null;
    if (el) {
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        /* older browsers without smooth-scroll fall back to default jump */
      }
      // Native input -> focus directly. Taiga controls -> focus the first
      // child input/select/textarea inside the host.
      const focusable =
        (el as HTMLInputElement).focus
          ? el
          : (el.querySelector(
              "input, select, textarea, [tabindex]",
            ) as HTMLElement | null);
      try {
        focusable?.focus({ preventScroll: false });
      } catch {
        /* ignore */
      }
      return path;
    }
  }
  return path;
}

function escapeSelector(value: string): string {
  return value.replace(/(["\\])/g, "\\$1");
}
